import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, Palmtree, X } from "lucide-react";
import { useLanguage } from "../i18n";
import { LINKS } from "../data";

export const WhatsAppIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
  </svg>
);

export default function WhatsAppButton() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const w = t.whatsapp;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onClick);
    };
  }, [open]);

  const link = (msg) => `${LINKS.whatsapp}?text=${encodeURIComponent(msg)}`;

  return (
    <div ref={ref} className="fixed bottom-[9.5rem] right-6 z-[70] lg:bottom-[5.75rem]">
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="whatsapp-popover"
            role="dialog"
            aria-label={w.title}
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-[4.25rem] right-0 w-64 rounded-3xl bg-white p-4 shadow-[0_24px_60px_-15px_rgba(15,43,58,0.45)] ring-1 ring-navy/10"
          >
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-navy">{w.title}</p>
              <button
                data-testid="whatsapp-close-button"
                onClick={() => setOpen(false)}
                aria-label={w.close}
                className="flex h-7 w-7 items-center justify-center rounded-full text-mute transition-colors hover:bg-sand hover:text-navy"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-3 space-y-2">
              <a
                data-testid="whatsapp-choice-guest"
                data-track="whatsapp_click_guest"
                href={link(w.guestMsg)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-2xl bg-sand px-4 py-3 text-sm font-bold text-navy transition-colors hover:bg-navy hover:text-white"
              >
                <Palmtree className="h-4 w-4 shrink-0" />
                {w.guest}
              </a>
              <a
                data-testid="whatsapp-choice-owner"
                data-track="whatsapp_click_owner"
                href={link(w.ownerMsg)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-2xl bg-sand px-4 py-3 text-sm font-bold text-navy transition-colors hover:bg-navy hover:text-white"
              >
                <Building2 className="h-4 w-4 shrink-0" />
                {w.owner}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="whatsapp-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="WhatsApp"
        aria-expanded={open}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2.4, type: "spring", stiffness: 260, damping: 20 }}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-navy shadow-[0_16px_40px_-10px_rgba(15,43,58,0.5)] ring-1 ring-navy/15 transition-[transform,box-shadow,background-color] duration-300 hover:scale-110 hover:bg-sand"
      >
        {WhatsAppIcon}
      </motion.button>
    </div>
  );
}
